import { useEffect, useState } from "react";
import { Radio, Users } from "lucide-react";
import { PageHeader, useTitle } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { api, asset } from "@/lib/api";
import { useSiteConfig } from "@/context/SiteConfig";
import { STREAMERS, FACES, IMAGES } from "@/data/content";

const FALLBACK = STREAMERS.map((s) => ({ ...s, image: FACES[s.face] }));

export default function Streamers() {
  useTitle("Streamers | Aurora Springs RP | RedM Roleplay", "Watch Aurora Springs RP streamers live — community creators broadcasting Wild West roleplay from our RedM server.");
  const { img } = useSiteConfig();
  const [items, setItems] = useState(FALLBACK);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/streamers-live").then(({ data }) => { if (alive && data?.streamers?.length) setItems(data.streamers); }).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const sorted = [...items].sort((a, b) => Number(b.live) - Number(a.live));

  return (
    <>
      <PageHeader overline="Watch the frontier live" title="Content Creators" image={img("streamers", IMAGES.duskRider)}
        sub="Catch Aurora Springs through the eyes of our storytellers. Tune in when the red light is on." />
      <section className="mx-auto max-w-7xl px-6 sm:px-10 py-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {sorted.map((s, i) => (
          <Reveal key={s.id || s.name} delay={i * 0.06}>
            <a href={s.url || undefined} target="_blank" rel="noreferrer" data-testid={`streamer-card-${i}`}
              className={`group relative block overflow-hidden border h-full ${s.live ? "border-destructive/40" : "border-[#2A201A]"} bg-surface/40 hover:border-teal/40 transition-colors ${s.url ? "cursor-pointer" : "cursor-default"}`}>
              <div className="relative h-56 overflow-hidden">
                <img src={asset(s.image)} alt={s.name} className="h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
                {s.live ? (
                  <span data-testid={`streamer-live-${i}`} className="absolute top-4 left-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest bg-destructive text-white px-3 py-1.5">
                    <Radio className="h-3 w-3 animate-pulse-dot" /> Live
                  </span>
                ) : (
                  <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest bg-obsidian/70 text-[#8F877C] px-3 py-1.5 border border-[#2A201A]">Offline</span>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-display text-2xl font-light">{s.name}</h3>
                    <p className="font-mono text-xs text-[#8F877C] mt-1">{s.platform}</p>
                  </div>
                  {s.live && s.viewers > 0 && <span className="inline-flex items-center gap-1.5 font-mono text-xs text-teal"><Users className="h-3.5 w-3.5" />{s.viewers}</span>}
                </div>
                {s.live && s.title && <p className="mt-3 text-xs text-[#b8b0a4] line-clamp-2">{s.title}</p>}
              </div>
            </a>
          </Reveal>
        ))}
      </section>
    </>
  );
}
